import { useQueryClient } from "@tanstack/react-query";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";

import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  publishedCmmdSetupPreferences,
  updateCmmdForgeAccountPreferences,
} from "../../cmmd/forgeAccountPreferences";
import { usePrimaryEnvironmentDescriptor } from "../../environments/primary";
import { useProjectsAcrossEnvironments } from "../../hooks/useProjectsAcrossEnvironments";
import { shouldUseCmmdFirstAccountSetupState } from "../../lib/firstAccountSetup";
import { getCurrentPrimaryServerApi } from "../../localApi";
import { useCompleteOnboarding } from "../../onboarding/firstRun";
import { applySettingsUpdated, useServerProviders } from "../../rpc/serverState";
import type { Project } from "../../types";
import { ForgeWordmark } from "../branding/ForgeWordmark";
import { Dialog } from "../ui/dialog";
import { toastManager } from "../ui/toast";
import { buildProviderRows } from "./firstAccountSetupData";
import { shouldSkipWizardOnContinue } from "./welcomeConnectStep.logic";
import { AgentsStep, ConnectStep, ProjectsStep, projectKey } from "./WelcomeWizardSteps";
import { WizardHeader, WizardPanel, WizardPopup, WizardSteps } from "./wizard";
import { completeWelcomeSetupState } from "./welcomeCompletion.logic";

/**
 * First-run welcome wizard, ported from upstream's
 * `components/onboarding/WelcomeWizard.tsx` onto forge's auth lanes. CMMD is
 * already the signed-in identity by the time the wizard mounts (the first-run
 * gate renders it only for authenticated browsers), so the connection step
 * surfaces the account's Forge Environments instead of upstream's pairing
 * flow, and machine-level flows (pairing, provider sign-in) stay owned by the
 * surfaces that already have them (/pair-machine, provider settings). Steps
 * per upstream: connection, agents, projects; the wizard completes to a first
 * thread and is skippable at the last step.
 */

const WIZARD_STAGES = [
  { id: "connect", label: "Connect" },
  { id: "agents", label: "Agents" },
  { id: "projects", label: "Projects" },
] as const;

type WizardStep = (typeof WIZARD_STAGES)[number]["id"];

function wizardCompletionFailedToast(): void {
  toastManager.add({
    type: "error",
    title: "Could not finish setup",
    description: "Your settings could not be saved. Try again.",
  });
}

function setupSaveFailedToast(): void {
  toastManager.add({
    type: "error",
    title: "Could not save setup",
    description: "Forge could not confirm setup completion. Try again.",
  });
}

function useWelcomeFinish(input: {
  readonly useCmmdSetupState: boolean;
  readonly onDone: (projectRef?: ScopedProjectRef) => void;
}): { readonly finish: (projectRef?: ScopedProjectRef) => void; readonly isFinishing: boolean } {
  const { onDone, useCmmdSetupState } = input;
  const completeOnboarding = useCompleteOnboarding();
  const queryClient = useQueryClient();
  const [isFinishing, setIsFinishing] = useState(false);
  const finishingRef = useRef(false);

  const finish = useCallback(
    (projectRef?: ScopedProjectRef) => {
      if (finishingRef.current) return;
      finishingRef.current = true;
      setIsFinishing(true);
      void (async () => {
        // Setup persistence first: a completion timestamp without a persisted
        // setup state would let a reload skip straight past the gate into an
        // unfinished setup.
        const setupSaved = await completeWelcomeSetupState({
          useCmmdSetupState,
          updateServerSettings: (patch) => getCurrentPrimaryServerApi().updateSettings(patch),
          onServerSettingsPersisted: applySettingsUpdated,
          publishCmmdSetupCompletion: () => {
            const preferences = publishedCmmdSetupPreferences({
              status: "completed",
              activeStepId: "first-run",
            });
            if (preferences === null) {
              return Promise.resolve();
            }
            return updateCmmdForgeAccountPreferences({ queryClient, preferences }).then(
              () => undefined,
            );
          },
        });
        if (!setupSaved) {
          setupSaveFailedToast();
          finishingRef.current = false;
          setIsFinishing(false);
          return;
        }
        try {
          await completeOnboarding();
        } catch {
          wizardCompletionFailedToast();
          finishingRef.current = false;
          setIsFinishing(false);
          return;
        }
        onDone(projectRef);
      })();
    },
    [completeOnboarding, onDone, queryClient, useCmmdSetupState],
  );

  return { finish, isFinishing };
}

function useWelcomeProjects(input: {
  readonly isProjectsStep: boolean;
  readonly selectedProjectKey: string | null;
  readonly onSelectionChange: (key: string) => void;
}): {
  readonly projects: readonly Project[];
  readonly selectedProject: Project | null;
} {
  const projects = useProjectsAcrossEnvironments();
  const selectedProject = useMemo(
    () => projects.find((project) => projectKey(project) === input.selectedProjectKey) ?? null,
    [input.selectedProjectKey, projects],
  );

  // Default the project choice to the first project so Continue is live as
  // soon as the step opens.
  useEffect(() => {
    if (!input.isProjectsStep || input.selectedProjectKey !== null || projects.length === 0) {
      return;
    }
    input.onSelectionChange(projectKey(projects[0]!));
  }, [input, projects]);

  return { projects, selectedProject };
}

export function WelcomeWizard({
  onDone,
}: {
  readonly onDone: (projectRef?: ScopedProjectRef) => void;
}): ReactElement {
  const primaryEnvironmentDescriptor = usePrimaryEnvironmentDescriptor();
  const useCmmdSetupState = primaryEnvironmentDescriptor
    ? shouldUseCmmdFirstAccountSetupState(primaryEnvironmentDescriptor)
    : false;
  const [step, setStep] = useState<WizardStep>("connect");
  const [selectedProjectKey, setSelectedProjectKey] = useState<string | null>(null);
  const { finish, isFinishing } = useWelcomeFinish({ onDone, useCmmdSetupState });

  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();
  const providers = useServerProviders();
  const providerRows = useMemo(() => buildProviderRows(providers, null, null), [providers]);
  const { projects, selectedProject } = useWelcomeProjects({
    isProjectsStep: step === "projects",
    onSelectionChange: setSelectedProjectKey,
    selectedProjectKey,
  });

  const stageIndex = WIZARD_STAGES.findIndex((stage) => stage.id === step);

  return (
    <Dialog open disablePointerDismissal onOpenChange={(_, event) => event.cancel()}>
      <WizardPopup showCloseButton={false}>
        <WizardHeader
          title="Set up Forge"
          identity={
            <div className="flex items-center" role="img" aria-label="Forge">
              <ForgeWordmark />
            </div>
          }
        >
          <WizardSteps
            steps={WIZARD_STAGES.map((stage) => stage.label)}
            currentStep={stageIndex}
            isStepDisabled={(index) => isFinishing || index >= stageIndex}
            onStepChange={(index) => {
              if (isFinishing || index > stageIndex) return;
              setStep(WIZARD_STAGES[index]!.id);
            }}
          />
        </WizardHeader>

        <WizardPanel>
          {step === "connect" ? (
            <ConnectStep
              environments={cloudCatalog.environments}
              onContinue={() => {
                // A suspended Environment is already provisioned; this
                // account does not need first-run onboarding, so Continue
                // skips the rest of the wizard instead of walking through it
                // (CMMD-Center/forge#3626). finish() with no project ref is
                // the wizard's existing skip action.
                if (shouldSkipWizardOnContinue(cloudCatalog.environments)) {
                  finish();
                  return;
                }
                setStep("agents");
              }}
            />
          ) : step === "agents" ? (
            <AgentsStep providerRows={providerRows} onContinue={() => setStep("projects")} />
          ) : (
            <ProjectsStep
              projects={projects}
              selectedProjectKey={selectedProjectKey}
              isFinishing={isFinishing}
              onSelectionChange={setSelectedProjectKey}
              onSkip={() => finish()}
              onStart={() =>
                finish(
                  selectedProject
                    ? scopeProjectRef(selectedProject.environmentId, selectedProject.id)
                    : undefined,
                )
              }
            />
          )}
        </WizardPanel>
      </WizardPopup>
    </Dialog>
  );
}
